import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@apollo/client';
import styled from 'styled-components';
import { Modal } from 'src/components/modals/comps';
import Select, { MultiSelect } from 'src/components/form/Select';
import Button from 'src/components/form/Button';
import { InputLabel, Poppins, Spacer } from 'src/common';
import { showSuccessToast } from 'src/components/Toast';
import { useAuth } from 'src/state/auth';
import { ScenarioGroupSummary } from 'src/graphql';
import {
  UPSERT_SCENARIO_PACK_FEATURE,
  UpsertScenarioPackFeatureMutation,
  UpsertScenarioPackFeatureMutationVariables,
} from 'src/graphql/contentImport';

const StyledModal = styled(Modal)`
  width: 760px;
  padding: 0 !important;
`;

const Content = styled.div`
  padding: 34px 30px 30px;
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.white};
  padding: 20px;
`;

const SummaryCard = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.stroke};
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.cultured};
  padding: 16px 18px;
`;

const ButtonsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

interface GroupOption {
  value: string;
  label: string;
}

interface OrgUnitOption {
  value: string;
  label: string;
}

interface PublishScenarioPackModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPublished: () => Promise<void> | void;
  groups: ScenarioGroupSummary[];
}

const scenarioPackFeatureKey = (groupId: string) => `scenario-pack.custom.${groupId.trim().toLowerCase()}`;

export const PublishScenarioPackModal: React.FC<PublishScenarioPackModalProps> = ({
  isOpen,
  onClose,
  onPublished,
  groups,
}) => {
  const { flatOrganisations, workspaceId } = useAuth();

  const [selectedGroupId, setSelectedGroupId] = useState<string>('');
  const [selectedOrganisationIds, setSelectedOrganisationIds] = useState<string[]>([]);
  const [selectedWorkspaceIds, setSelectedWorkspaceIds] = useState<string[]>([]);
  const [error, setError] = useState('');

  const [runUpsertFeature, { loading: isPublishing }] = useMutation<
    UpsertScenarioPackFeatureMutation,
    UpsertScenarioPackFeatureMutationVariables
  >(UPSERT_SCENARIO_PACK_FEATURE);

  const groupOptions = useMemo<GroupOption[]>(() => {
    return (groups || [])
      .map((group) => ({
        value: group.id,
        label: group.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [groups]);

  const organisationOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'O' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const workspaceOptions = useMemo<OrgUnitOption[]>(() => {
    return (flatOrganisations || [])
      .filter((org) => org.recordType === 'W' && !org.isArchived)
      .map((org) => ({
        value: org.id,
        label: org.name,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [flatOrganisations]);

  const selectedGroup = useMemo(
    () => groups.find((group) => group.id === selectedGroupId) || null,
    [groups, selectedGroupId],
  );

  useEffect(() => {
    if (!isOpen) return;

    setSelectedGroupId(groups[0]?.id || '');
    setSelectedOrganisationIds([]);
    setSelectedWorkspaceIds(workspaceId ? [workspaceId] : []);
    setError('');
  }, [isOpen, groups, workspaceId]);

  const handleClose = () => {
    if (isPublishing) return;
    onClose();
  };

  const hasTargets = selectedOrganisationIds.length > 0 || selectedWorkspaceIds.length > 0;
  const canPublish = !!selectedGroup && hasTargets && !isPublishing;

  const groupSelectValue = groupOptions.find((option) => option.value === selectedGroupId) || null;

  const onPublish = async () => {
    if (!selectedGroup || !hasTargets) return;

    setError('');

    try {
      await runUpsertFeature({
        variables: {
          name: selectedGroup.featureKey || scenarioPackFeatureKey(selectedGroup.id),
          general: false,
          organisations: selectedOrganisationIds,
          workspaces: selectedWorkspaceIds,
        },
      });

      await onPublished();
      showSuccessToast({ title: 'Scenario pack published' });
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Scenario pack publish failed.');
    }
  };

  return (
    <StyledModal isOpen={isOpen} onRequestClose={handleClose} persistent={isPublishing}>
      <Content>
        <Poppins className="m-title" px={32}>
          Publish scenario pack
        </Poppins>

        <Spacer $px={18} />
        <Panel>
          <InputLabel>SCENARIO PACK</InputLabel>
          <Spacer $px={8} />
          <Select
            options={groupOptions}
            value={groupSelectValue}
            onChange={(nextValue: unknown) => setSelectedGroupId(((nextValue as GroupOption | null)?.value || '').trim())}
            placeholder="Select scenario pack"
            isDisabled={isPublishing || !groupOptions.length}
          />

          {selectedGroup && (
            <>
              <Spacer $px={16} />
              <SummaryCard>
                <Poppins px={14}>
                  {selectedGroup.name} • {selectedGroup.scenarios?.length || 0} scenarios
                </Poppins>
              </SummaryCard>
            </>
          )}

          <Spacer $px={16} />
          <InputLabel>PUBLISH TO ORGANISATIONS</InputLabel>
          <Spacer $px={8} />
          <MultiSelect
            options={organisationOptions}
            value={selectedOrganisationIds}
            onChange={setSelectedOrganisationIds}
            placeholder="Select organisations"
            selectedLabel="selected"
          />

          <Spacer $px={16} />
          <InputLabel>PUBLISH TO WORKSPACES</InputLabel>
          <Spacer $px={8} />
          <MultiSelect
            options={workspaceOptions}
            value={selectedWorkspaceIds}
            onChange={setSelectedWorkspaceIds}
            placeholder="Select workspaces"
            selectedLabel="selected"
          />

          {!hasTargets && (
            <>
              <Spacer $px={12} />
              <Poppins px={14} color="error">
                Select at least one organisation or workspace to publish this scenario pack.
              </Poppins>
            </>
          )}
        </Panel>

        {!!error && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              {error}
            </Poppins>
          </>
        )}

        {!groupOptions.length && (
          <>
            <Spacer $px={14} />
            <Poppins px={14} color="error">
              No scenario packs available to publish in this workspace.
            </Poppins>
          </>
        )}

        <Spacer $px={20} />
        <ButtonsRow>
          <Button primary style={{ minWidth: 280 }} onClick={onPublish} disabled={!canPublish} loading={isPublishing}>
            Publish scenario pack
          </Button>
        </ButtonsRow>
      </Content>
    </StyledModal>
  );
};
